import { ChangeEvent, FormEvent, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import useAllInvestment from "../../custom-hooks/useAllInvestment";
import ButtonLoader, { Loader } from "../../UI/Loaders/Loaders";
import CustomSelectDropdown from "../CustomHTMLElements/CustomSelectDropdown";
import Paginator from "../Pagination/Pagination.component";
import { showModal } from "../../redux/actions/modal";
import { formatMoney } from "../../helpers/formatter";
import { attachClassNameToTag } from "../../helpers/attachClassNameToTag";
import { getTokenFromLocalStorage } from "../../helpers/localStorage";
import { BASE_URL } from "../../appConstants";
import { appInsights } from "../../config/appInsights";
import useInvestmentCurrencies from "../../custom-hooks/useInvestmentCurrencies";
import {
  handleRollOverModalToggle,
  InvestmentStatus,
} from "../Investor/Tabs/EachInvestorInvestment/helper";
import CloseInvestmentModal from "../../NewComponents/CloseInvestmentModal";
import useInvestmentClosure from "../../custom-hooks/useInvestmentClosure";
import * as ajaxEndpoints from "../../api/ajax-endpoints";
import { message } from "antd";
import useGetInvestmentStatus from "../../custom-hooks/useGetInvestmentStatus";
import { ISelectedRecord } from "../Investor/Tabs/EachInvestorInvestment/EachInvestorInvestment";
import RollOverModal from "../Investor/Components/RollOverModal/RollOverModal";
import PrincipalOrInterestModal from "../Investor/Components/PrincipalOrInterestModal/PrincipalOrInterestModal";
import { RollOverType } from "../Investor/Tabs/EachInvestorInvestment/types";
import useGetDateSearchMode from "../../custom-hooks/useGetDateSearchMode";

export interface IProps {
  id: number;
  name: string;
  description: string;
}

const AllInvestments = () => {
  const [preState, setPreState] = useState({
    pageNumber: 1,
    size: 20,
  });

  const [downloadLoading, setDownloadLoading] = useState(false);
  const [state, setState] = useState({
    pageNumber: 1,
    size: 20,
    currency: "",
    startDate: "",
    endDate: "",
    searchValue: "",
    aggregatorId: "",
    dateSearchMode: 0,
    investmentStatus: 0,
  });
  const [investmentID, setInvestmentID] = useState<number | null>(null);
  const [maturityDate, setMaturityDate] = useState<string>("");
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<
    ISelectedRecord | undefined
  >();
  const [selectedRollOverType, setSelectedRollOverType] =
    useState<RollOverType | null>(null);

  const { data: _investmentStatus, isLoading: isLoadingInvestmentStatus } =
    useGetInvestmentStatus();
  const { data: dateMode, isLoading: isLoadingDateMode } =
    useGetDateSearchMode();

  useEffect(() => {
    appInsights.trackPageView({
      name: "Investment Info  - (AllInvestments.jsx)",
      isLoggedIn: true,
    });
  }, []);

  const dispatch = useDispatch();
  const history = useHistory();

  const { data: investmentCurrencies } = useInvestmentCurrencies();

  const onClear = () => {
    setState({
      pageNumber: 1,
      size: 20,
      currency: "",
      startDate: "",
      endDate: "",
      searchValue: "",
      aggregatorId: "",
      dateSearchMode: 0,
      investmentStatus: 0,
    });
  };

  const handleDownload = () => {
    setDownloadLoading(true);

    let token = getTokenFromLocalStorage();
    let queryParams = "";
    if (state.startDate && state.endDate) {
      queryParams = `?FromDate=${state.startDate}&ToDate=${state.endDate}`;
    }
    if (state.aggregatorId) {
      queryParams = queryParams
        ? `${queryParams}&AggregatorId=${state.aggregatorId}`
        : `?AggregatorId=${state.aggregatorId}`;
    }
    if (state.searchValue) {
      queryParams = queryParams
        ? `${queryParams}&FunderName=${state.searchValue}`
        : `?FunderName=${state.searchValue}`;
    }
    if (state.currency) {
      queryParams = queryParams
        ? `${queryParams}&Currency=${state.currency}`
        : `?Currency=${state.currency}`;
    }
    setPreState(state);
    fetch(BASE_URL + `/account/download_investments${queryParams}`, {
      method: "GET",
      headers: {
        "Content-type": "application/json",
        Authorization: "Bearer " + token,
      },
      // body: JSON.stringify(data)
    })
      .then((response) => {
        setDownloadLoading(false);
        if (response.status === 200 || response.status === 201) {
          response.blob().then((blob) => {
            const url = window.URL.createObjectURL(new Blob([blob]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute(
              "download",
              `${
                state.startDate && state.endDate
                  ? `Investments from ${state.startDate} to ${state.endDate}.csv`
                  : `Investments.csv`
              }`,
            );
            link.click();
          });
        }

        if (response.status === 403 || response.status === 401) {
          localStorage.removeItem("sso_auth_token");
          history.push("/login");
        }

        if (response.status >= 500) {
          // this.setState({ error: "Something went wrong. Please check your internet connection and try again" });
        }
      })
      .catch((error: any) => {
        appInsights.trackException({
          exception: error,
          properties: {
            fileName: "AllInvestments.jsx",
          },
        });
        // this.setState({ loading: false, error: "Something went wrong. Please check your internet connection and try again" });
        setDownloadLoading(false);
      });
  };
  const [count, setCount] = useState(null);

  const { data, status, error, refetch } = useAllInvestment(preState);
  const {
    mutate: closeInvestment,
    isLoading: isLoadingInvestmentClosure,
    error: investmentClosureError,
    data: closureData,
  } = useInvestmentClosure(
    ajaxEndpoints.CLOSE_WITHDRAW_INVESTMENT,
    (responseData: any) => {
      message.success(
        responseData?.message || "Investment closed successfully!",
      );
      setIsConfirmationModalOpen(false);
      refetch();
    },
  );

  const handleInvestmentClosure = async (closeDate: string) => {
    await closeInvestment({
      investment_id: investmentID,
      close_date: closeDate,
    });
  };

  if (data) {
    if (count === null) {
      setCount(data.totalCount);
    }
  }
  const resetTable = () => {
    setCount(null);
    setPreState({ ...state, pageNumber: 1 });
  };

  const modalHandler = (modalData: string, type: string) => {
    dispatch(
      showModal({
        modalIdentifier: "audittrail",
        dataRecord: modalData,
        action: "customrange",
        type,
      }),
    );
  };
  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setState({ ...state, [name]: value });
  };

  const filterData = (e: FormEvent) => {
    e.preventDefault();
    const diffTime =
      new Date(state.endDate).valueOf() - new Date(state.startDate).valueOf();
    if (diffTime < 0) {
      modalHandler("", "alert");
      return;
    } else {
      setPreState(state);
    }
    resetTable();
  };
  let RenderedComponent = null;
  if (!data && status !== "loading") {
    RenderedComponent = (
      <div>
        <div className="grid__padding animated fadeInRight">
          <h4 className="center-txt">
            <i className="fas fa-bell" /> Unable to Fetch Investments
          </h4>
        </div>
      </div>
    );
  } else if (status === "loading") {
    RenderedComponent = (
      <Loader centered={true} text="Loading Investments..." />
    );
  } else {
    if (error) {
      RenderedComponent = (
        <div>
          <div className="grid__padding animated fadeInRight">
            <h4 className="center-txt p-y-5">
              <i className="fas fa-bell" /> There was an issue while fetching
              investments, Please Reload The Page{" "}
            </h4>
          </div>
        </div>
      );
    } else if (
      (data && data.data && data.data.length === 0) ||
      (data && !data.data)
    ) {
      RenderedComponent = (
        <div className="centerLoader">
          <div className="grid__padding animated fadeInRight">
            <h4 className="center-txt p-y-5">
              <i className="fas fa-bell" /> There are currently no Investment.
            </h4>
          </div>
        </div>
      );
    } else {
      RenderedComponent = (
        <div className="row m-x-sm m-y-2 grid__padding">
          <div className="table-responsive">
            <table className="table">
              <thead className="bg-lighter-grey">
                <tr>
                  <th>Ref No</th>
                  <th>Investor</th>
                  <th>Amount Invested</th>
                  <th>Tenor</th>
                  <th>Monthly Interest</th>
                  <th>Status</th>
                  <th>Date Invested</th>
                  <th>Maturity Date</th>
                  <th>Amount Due</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.data &&
                  data.data.map((request: any, _id: number) => {
                    const {
                      funder_name,
                      principal_amount,
                      currency,
                      reference_number,
                      tenure,
                      monthly_interest_repayment,
                      effective_date,
                      maturity_date,
                      investment_status,
                      funder_id,
                      settlement_due,
                      investment_product_id,
                      id: investment_id,
                    } = request;
                    return (
                      <tr key={_id}>
                        <td>{reference_number}</td>
                        <td>{funder_name}</td>
                        <td>{formatMoney(principal_amount, currency)}</td>
                        <td>{tenure}</td>
                        <td>
                          {formatMoney(monthly_interest_repayment, currency)}
                        </td>
                        <td>
                          <span
                            className={attachClassNameToTag(investment_status)}
                          >
                            {attachClassNameToTag(investment_status)}
                          </span>
                        </td>
                        <td>
                          {effective_date
                            ? effective_date.substring(0, 10)
                            : "N/A"}
                        </td>
                        <td>
                          {maturity_date
                            ? maturity_date.substring(0, 10)
                            : "N/A"}
                        </td>
                        <td>{formatMoney(settlement_due, currency)}</td>
                        <td>
                          <div className="dropdown">
                            <button
                              className="btn zoom-element btn-slim dropdown-toggle"
                              type="button"
                              id="dropdownMenuButton"
                              data-toggle="dropdown"
                              aria-haspopup="true"
                              aria-expanded="false"
                            ></button>
                            <div
                              className="dropdown-menu"
                              style={{ textAlign: "center" }}
                              aria-labelledby="dropdownMenuButton"
                            >
                              {investment_status ===
                              InvestmentStatus.MATURED ? (
                                <span
                                  className="cursor-pointer dropdown-item"
                                  id="custom-rollover-button"
                                  onClick={() => {
                                    handleRollOverModalToggle();
                                    setSelectedRecord({
                                      ...request,
                                      investment_product_id,
                                    });
                                  }}
                                >
                                  ROLL OVER
                                </span>
                              ) : null}
                              <span
                                className="cursor-pointer dropdown-item"
                                onClick={() => {
                                  history.push({
                                    pathname: `/investment-details/${funder_id}`,
                                    state: {
                                      data: request,
                                    },
                                  });
                                }}
                              >
                                VIEW DETAILS
                              </span>
                              {investment_status ===
                              InvestmentStatus.MATURED ? (
                                <span
                                  className="cursor-pointer dropdown-item"
                                  onClick={() => {
                                    setIsConfirmationModalOpen(true);
                                    setInvestmentID(investment_id);
                                    setMaturityDate(maturity_date || "");
                                  }}
                                >
                                  CLOSE
                                </span>
                              ) : null}
                            </div>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>
      );
    }
  }

  if (isLoadingInvestmentStatus || isLoadingDateMode) {
    return <Loader />;
  }

  return (
    <div>
      <div className="datatable-wrapper">
        <div className="table-view speed-1x animated fadeInRight">
          <div className="d-flex flex-row flex-wrap mb-4">
            <h3 className="page-title">All Investments</h3>
            <button
              type="button"
              className="btn advancly-white-btn m-0"
              onClick={handleDownload}
              disabled={status === "loading" || downloadLoading}
            >
              <i className="fas fa-download m-r-1" />
              Download {downloadLoading && <ButtonLoader />}
            </button>
          </div>
          <div className="d-flex flex-row flex-wrap">
            <form className="d-flex flex-row flex-wrap" onSubmit={filterData}>
              <div className="input-group mr-3" style={{ maxWidth: "200px" }}>
                <input
                  type="search"
                  className="form-control"
                  placeholder="Funder Name..."
                  value={state.searchValue}
                  name="searchValue"
                  onChange={(e) => handleChange(e)}
                />
              </div>
              <div style={{ width: "100px" }} className="mr-3">
                <CustomSelectDropdown
                  onChange={(e) => handleChange(e)}
                  name="currency"
                  value={state.currency}
                  className="round user__form form-control"
                >
                  {investmentCurrencies &&
                    investmentCurrencies?.data.map(
                      (_currency, index: number) => {
                        return (
                          <option key={index} value={_currency?.currency_code}>
                            {_currency?.currency_code}
                          </option>
                        );
                      },
                    )}
                </CustomSelectDropdown>
              </div>
              <div style={{ width: "150px" }} className="mr-3">
                <CustomSelectDropdown
                  onChange={(e) => handleChange(e)}
                  name="investmentStatus"
                  value={state.investmentStatus}
                  className="round user__form form-control"
                >
                  <option value="">Select Status</option>
                  {_investmentStatus &&
                    _investmentStatus?.data?.map((status: IProps) => {
                      return (
                        <option value={status.id} key={status.description}>
                          {status.description}
                        </option>
                      );
                    })}
                </CustomSelectDropdown>
              </div>

              <div style={{ width: "150px" }} className="mr-3">
                <CustomSelectDropdown
                  onChange={(e) => handleChange(e)}
                  name="dateSearchMode"
                  value={state.dateSearchMode}
                  className="round user__form form-control"
                >
                  <option value="">Select Date Mode</option>
                  {dateMode &&
                    dateMode?.data?.map((mode: IProps) => {
                      return (
                        <option value={mode.id} key={mode.description}>
                          {mode.description}
                        </option>
                      );
                    })}
                </CustomSelectDropdown>
              </div>

              <div className="form-group mr-3 mt-3">
                <div className="input-group ">
                  <label className="mr-2">From </label>
                  <input
                    type="date"
                    name="startDate"
                    className="form-control"
                    value={state.startDate}
                    onChange={(e) => handleChange(e)}
                    disabled={!state.dateSearchMode}
                  />
                </div>
              </div>
              <div className="form-group mt-3 mr-3">
                <div className="input-group">
                  <label className="mr-2">To </label>
                  <input
                    type="date"
                    className="form-control"
                    name="endDate"
                    value={state.endDate}
                    onChange={(e) => handleChange(e)}
                    disabled={!state.dateSearchMode}
                  />
                </div>
              </div>
              <div style={{ marginBottom: "7px" }}>
                <button
                  type="submit"
                  className="btn advancly-btn btn-md"
                  value="submit"
                  disabled={status === "loading" || downloadLoading}
                >
                  Search
                </button>
              </div>
              <div style={{ marginBottom: "7px", marginLeft: "10px" }}>
                <button
                  type="submit"
                  className="btn advancly-btn btn-md"
                  onClick={onClear}
                >
                  Clear
                </button>
              </div>
            </form>
          </div>
          {/*Datatable || Loader || Error Message*/}
          {RenderedComponent}
          {data && data?.data && (
            <Paginator
              size={preState.size}
              page={preState.pageNumber}
              count={count}
              changeCurrentPage={(pageNumber: number) =>
                setPreState({ ...state, pageNumber })
              }
            />
          )}
        </div>
      </div>
      {isConfirmationModalOpen ? (
        <CloseInvestmentModal
          onConfirm={handleInvestmentClosure}
          onClose={() => {
            setIsConfirmationModalOpen(false);
            setInvestmentID(null);
            setMaturityDate("");
          }}
          isOpen={isConfirmationModalOpen}
          isLoading={isLoadingInvestmentClosure}
          maturityDate={maturityDate}
        />
      ) : null}
      <PrincipalOrInterestModal
        setSelectedRollOverType={setSelectedRollOverType}
      />
      {selectedRecord && (
        <>
          <RollOverModal
            selectedRollOverType={selectedRollOverType}
            selectedRecord={selectedRecord}
          />
        </>
      )}
    </div>
  );
};

export default AllInvestments;
